import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ExternalLink, Loader2 } from 'lucide-react';
import type { MeterDetailCustomer, MeterDetailInfo } from '../hooks/useMeterDetail';

function buildWaterScopePlusUrl(
  customerId: number,
  allSensorsGroupId: number,
  sensorId: number,
): string {
  return `https://plus.waterscope.us/portfolio/${customerId}/${allSensorsGroupId}/${allSensorsGroupId}/${sensorId}`;
}

interface MeterInfoPanelProps {
  detail: MeterDetailInfo | null;
  customers: MeterDetailCustomer[];
  isLoading: boolean;
  error: Error | null;
}

// Helper to render a key-value field
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className='text-xs font-medium text-muted-foreground'>{label}</p>
      <p className='text-sm font-mono'>
        {value ?? <span className='text-muted-foreground'>&mdash;</span>}
      </p>
    </div>
  );
}

export function MeterIdentityCard(
  { detail, isLoading, error }: Omit<MeterInfoPanelProps, 'customers'>,
) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className='flex items-center justify-center py-8'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          <span className='ml-2 text-sm text-muted-foreground'>Loading meter detail...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className='py-8'>
          <p className='text-sm text-destructive text-center'>Failed to load meter detail</p>
        </CardContent>
      </Card>
    );
  }

  if (!detail) {
    return (
      <Card>
        <CardContent className='py-8'>
          <p className='text-sm text-muted-foreground text-center'>
            No detail data available for this sensor
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-lg'>Identity & Location</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          <Field label='Meter ID (Device ID)' value={detail.deviceId} />
          <Field label='Device Serial' value={detail.deviceSerial} />
          <Field label='Meter Size' value={detail.meterSize} />
          <Field label='Consumer ID' value={detail.consumerId} />
          <Field label='Consumer Name' value={detail.consumerName} />
          <Field label='Address' value={detail.address} />
          <Field label='WS Address' value={detail.wsAddress} />
          <Field label='Latitude' value={detail.latitude} />
          <Field label='Longitude' value={detail.longitude} />
          <Field label='Timezone' value={detail.timezone} />
        </div>
      </CardContent>
    </Card>
  );
}

export function MeterCustomersCard(
  { customers, isLoading, sensorId }: {
    customers: MeterDetailCustomer[];
    isLoading: boolean;
    sensorId: number | null;
  },
) {
  if (isLoading) return null;

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-lg'>Customers ({customers.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {customers.length === 0
          ? <p className='text-sm text-muted-foreground'>No customers linked to this sensor</p>
          : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Account Name</TableHead>
                  <TableHead>WaterScope+</TableHead>
                  <TableHead>Account ID</TableHead>
                  <TableHead>Salesforce ID</TableHead>
                  <TableHead>Metron ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>RE Type</TableHead>
                  <TableHead>Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((cust) => {
                  const wsPlusUrl = sensorId != null && cust.allSensorsGroupId != null
                    ? buildWaterScopePlusUrl(cust.id, cust.allSensorsGroupId, sensorId)
                    : null;
                  return (
                    <TableRow key={cust.id}>
                      <TableCell className='font-mono'>{cust.id}</TableCell>
                      <TableCell>
                        <Link
                          to={`/customers/${cust.id}`}
                          className='text-primary hover:underline'
                        >
                          {cust.accountName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {wsPlusUrl
                          ? (
                            <a
                              href={wsPlusUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='inline-flex items-center gap-1 text-primary hover:underline'
                            >
                              Open
                              <ExternalLink className='h-3 w-3' />
                            </a>
                          )
                          : <span className='text-muted-foreground'>&mdash;</span>}
                      </TableCell>
                      <TableCell className='font-mono'>
                        {cust.accountId ?? <span>&mdash;</span>}
                      </TableCell>
                      <TableCell className='font-mono'>
                        {cust.salesforceId ?? <span>&mdash;</span>}
                      </TableCell>
                      <TableCell className='font-mono'>
                        {cust.metronId ?? <span>&mdash;</span>}
                      </TableCell>
                      <TableCell>{cust.accountType ?? <span>&mdash;</span>}</TableCell>
                      <TableCell>{cust.realEstateAccountType ?? <span>&mdash;</span>}</TableCell>
                      <TableCell>
                        <Badge variant={cust.active ? 'default' : 'secondary'}>
                          {cust.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
      </CardContent>
    </Card>
  );
}

export function MeterDetailCards({ detail }: { detail: MeterDetailInfo | null }) {
  if (!detail) return null;

  return (
    <div className='space-y-4'>
      {/* Device Configuration */}
      <Card>
        <CardHeader className='pb-3'>
          <CardTitle className='text-lg'>Device Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            <Field label='Meter Brand' value={detail.meterBrand} />
            <Field label='Device Description' value={detail.deviceDescription} />
            <Field label='WDI Device Description' value={detail.wdiDeviceDescription} />
            <Field label='Sensor Description' value={detail.sensorDescription} />
            <Field label='WDI Meter Size' value={detail.wdiMeterSize} />
            <Field label='Read Units' value={detail.readUnits} />
          </div>
        </CardContent>
      </Card>

      {/* Unified Metadata */}
      <Card>
        <CardHeader className='pb-3'>
          <CardTitle className='text-lg'>Unified Metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            <Field label='Meter Type' value={detail.meterType} />
            <Field label='UM Meter Size' value={detail.umMeterSize} />
            <Field label='Register Type' value={detail.registerType} />
            <Field label='Register Brand' value={detail.registerBrand} />
            <Field label='Log Interval' value={detail.logInterval} />
            <Field label='Turn Divider' value={detail.turnDivider} />
            <Field label='Index Ratio' value={detail.indexRatio} />
            <Field label='Transmit Scaling' value={detail.transmitScaling} />
            <Field label='First Digit Position' value={detail.firstDigitPosition} />
            <Field label='Decimal Position' value={detail.decimalPosition} />
            <Field label='LCD Underscores' value={detail.numLcdUnderscores} />
            <Field label='Hardware Version' value={detail.hardwareVersion} />
            <Field label='Firmware Version' value={detail.firmwareVersion} />
            <Field label='LCD Read' value={detail.lcdRead} />
            <Field
              label='Last Read Date'
              value={detail.lastReadDate ? new Date(detail.lastReadDate).toLocaleString() : null}
            />
            <Field
              label='Signal Quality'
              value={Array.isArray(detail.signalQuality)
                ? detail.signalQuality.join(', ')
                : detail.signalQuality}
            />
            <Field label='RSSI' value={detail.rssi} />
            <Field label='Broadcast Offset' value={detail.broadcastOffset} />
            <Field label='LTE TX Attempts' value={detail.numLteTxAttempts} />
            <Field label='Sets Performed' value={detail.numSetsPerformed} />
            <Field label='Gateways' value={detail.gateways} />
            <Field
              label='Metadata Updated'
              value={detail.metadataUpdatedAt
                ? new Date(detail.metadataUpdatedAt).toLocaleString()
                : null}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
